import React from 'react'
import { Text, SimpleGrid, GridItem, Box, Flex } from '@chakra-ui/react'

import EmptyState from '~components/shared/empty-state'
import {
    PatientFieldsFragment,
    PatientVaccinationRecordFieldsFragment,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import { useFormatDate } from '~utils/hooks/use-format-date'
import VaccinationRecordsActions from '~components/clients-patients/detailed/patient/records/vaccinations/actions'

interface Props {
    patient: PatientFieldsFragment
}

export default function PatientVaccines({ patient }: Props) {
    const { clinic } = useClinic()

    const { patient_vaccination_records: vaccineRecords } = patient

    const hideSoc = clinic?.settings?.hide_soc_vaccinations
    const hideHistoric = clinic?.settings?.hide_historic_vaccinations

    const allVaccines =
        vaccineRecords?.filter(vax => {
            if (!vax.is_active) return false

            if (hideSoc && vax.is_soc) return false
            if (hideHistoric && !vax.is_soc) return false
            return true
        }) ?? []

    const dedupedVaccines = dedupeVaccines(allVaccines)
    const hasVaccines = dedupedVaccines.length > 0

    return (
        <Box w="full">
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                mb={3}
                wrap="wrap"
            >
                <Text fontWeight="semibold">Vaccines</Text>
                <VaccinationRecordsActions
                    patient={patient}
                    vaccines={dedupedVaccines}
                />
            </Flex>
            {!hasVaccines ? (
                <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
                    <EmptyState title="No vaccines" subtitle=" " py={6} />
                </Box>
            ) : (
                <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
                    <SimpleGrid columns={4} mb={2} w="full">
                        <GridItem colSpan={2}>
                            <Text
                                fontSize="xs"
                                textTransform="uppercase"
                                fontWeight="medium"
                            >
                                Vaccine
                            </Text>
                        </GridItem>
                        <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            fontWeight="medium"
                            textAlign="right"
                        >
                            Administered
                        </Text>
                        <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            fontWeight="medium"
                            textAlign="right"
                        >
                            Next Due
                        </Text>
                    </SimpleGrid>
                    {dedupedVaccines.map(vaccine => (
                        <VaccineRecord key={vaccine.id} vaccine={vaccine} />
                    ))}
                </Box>
            )}
        </Box>
    )
}

interface VaccineProps {
    vaccine: PatientVaccinationRecordFieldsFragment
}

function VaccineRecord({ vaccine }: VaccineProps) {
    const { formatDate } = useFormatDate()

    const { administered_date, due_date } = vaccine

    const isOverdue = due_date && due_date < Date.now()

    const displayName =
        vaccine.product?.display_name ||
        vaccine.product?.name ||
        vaccine.vaccine_name

    return (
        <SimpleGrid columns={4} mb={2} w="full">
            <GridItem colSpan={2}>
                <Text fontWeight="semibold" fontSize="sm">
                    {displayName}
                </Text>
            </GridItem>
            <Text textAlign="right" fontSize="sm" color="gray.500">
                {administered_date ? formatDate(administered_date) : 'N/A'}
            </Text>
            <Text
                color={isOverdue ? 'red.500' : 'gray.500'}
                fontSize="sm"
                textAlign="right"
            >
                {due_date ? formatDate(due_date) : 'N/A'}
            </Text>
        </SimpleGrid>
    )
}

export function dedupeVaccines(
    vaccines: PatientVaccinationRecordFieldsFragment[],
) {
    // Newest vaccines first
    const vaccinesSortedByDate = [...vaccines].sort((a, b) => {
        if (a.due_date && b.due_date) {
            return b.due_date - a.due_date
        }

        if (a.due_date && !b.due_date) {
            return -1
        }

        if (!a.due_date && b.due_date) {
            return 1
        }

        if (a.administered_date && b.administered_date) {
            return b.administered_date - a.administered_date
        }

        if (a.administered_date && !b.administered_date) {
            return -1
        }

        if (!a.administered_date && b.administered_date) {
            return 1
        }

        return 0
    })

    // Remove duplicates by display name (product.display_name || product.name || vaccine_name)
    const seen = new Set<string>()
    const dedupedVaccines = vaccinesSortedByDate.filter(v => {
        const key =
            v.product?.display_name || v.product?.name || v.vaccine_name || ''
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })

    const resortedVaccines = dedupedVaccines.sort((a, b) => {
        if (a.due_date && b.due_date) {
            return a.due_date - b.due_date
        }

        return 0
    })

    return resortedVaccines
}
